import { useCallback, useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import type { Clinic, IntegrationType } from '../types'
import type { EyemedLoginState } from '../../shared/eyemedLogin'
import {
  saveIntegrationCredentials,
  deleteIntegrationCredentials,
  type SaveIntegrationCredentialsInput,
} from '../services/integrations'
import { scrubVendorText } from '../utils/scrubVendorText'
import { eyemedRowInput, eyemedRowStatus, type EyemedRowStatus } from '../utils/eyemedRowStatus'
import { listEyemedLoginStates } from '../services/eyemedSetup'
import EyemedSetupModal from './EyemedSetupModal'
import revolutionLogo from '../assets/revolution-logo.svg'
import vspLogo from '../assets/vsp-logo.png'
import eyemedLogo from '../assets/eyemed-logo.svg'

interface IntegrationsListProps {
  clinics: Clinic[]
  orgId: string
  isAdmin: boolean
  /** May act on the tenant (not a view-mode staff session): runs the EyeMed code setup. */
  canWrite: boolean
  onIntegrationSaved: () => void
}

const PASSWORD_PLACEHOLDER = '********'

const INTEGRATIONS: { type: IntegrationType; title: string; description: string; logo: string }[] = [
  { type: 'revolution_ehr', title: 'Revolution', description: 'Practice management and EHR integration', logo: revolutionLogo },
  { type: 'vsp', title: 'VSP', description: 'Vision Service Plan eligibility verification', logo: vspLogo },
  { type: 'eyemed', title: 'EyeMed', description: 'EyeMed vision eligibility verification', logo: eyemedLogo },
]

function clinicLocation(c: Clinic): string {
  if (c.city && c.state) return `${c.city}, ${c.state}`
  return ''
}

/** A clinic's configured integrations; its saved EyeMed login counts only when
 *  nothing is left for the clinic to do (`eyemedRowStatus`). */
function configuredCount(c: Clinic, eyemed: EyemedRowStatus): number {
  return INTEGRATIONS.filter(i => c.integrations?.[i.type] && (i.type !== 'eyemed' || eyemed.counts)).length
}

/** The CSS modifier for a status tone; green is the page's existing `active`. */
function toneModifier(tone: EyemedRowStatus['tone']): string {
  return tone === 'green' ? 'active' : tone
}

export default function IntegrationsList({ clinics, orgId, isAdmin, canWrite, onIntegrationSaved }: IntegrationsListProps) {
  const [expandedClinicId, setExpandedClinicId] = useState<string | null>(clinics[0]?.id ?? null)

  // Each clinic's EyeMed login state, from the setup callable (no login state
  // is client-readable): null while loading, 'failed' when the fetch failed.
  const [eyemedStates, setEyemedStates] = useState<Map<string, EyemedLoginState> | 'failed' | null>(null)
  const [setupFor, setSetupFor] = useState<{ clinicId: string; clinicName: string; username: string } | null>(null)
  const loadEyemedStates = useCallback(async (): Promise<void> => {
    try {
      const rows = await listEyemedLoginStates(orgId)
      setEyemedStates(new Map(rows.map(r => [r.clinicId, r.state])))
    } catch (err: unknown) {
      Sentry.captureException(err)
      setEyemedStates('failed')
    }
  }, [orgId])
  useEffect(() => { void loadEyemedStates() }, [loadEyemedStates])

  // Modal state
  const [modalClinicId, setModalClinicId] = useState<string | null>(null)
  const [modalType, setModalType] = useState<IntegrationType>('revolution_ehr')
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [clinicField, setClinicField] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [attempted, setAttempted] = useState(false)

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<{ clinicId: string; type: IntegrationType; title: string } | null>(null)
  const [deleting, setDeleting] = useState(false)

  const modalClinic = clinics.find(c => c.id === modalClinicId)
  const modalInfo = INTEGRATIONS.find(i => i.type === modalType)

  function toggleClinic(id: string) {
    setExpandedClinicId(prev => prev === id ? null : id)
  }

  function openAdd(clinicId: string, type: IntegrationType) {
    const clinic = clinics.find(c => c.id === clinicId)
    setModalClinicId(clinicId)
    setModalType(type)
    setModalMode('add')
    setUsername('')
    setPassword('')
    // Default the Revolution clinic field to the clinic's name; admin can override.
    setClinicField(type === 'revolution_ehr' ? (clinic?.name ?? '') : '')
    setShowPassword(false)
    setSaveError(null)
    setAttempted(false)
  }

  function openEdit(clinicId: string, type: IntegrationType) {
    const clinic = clinics.find(c => c.id === clinicId)
    const config = clinic?.integrations?.[type]
    setModalClinicId(clinicId)
    setModalType(type)
    setModalMode('edit')
    setUsername(config?.username ?? '')
    setPassword(PASSWORD_PLACEHOLDER)
    // Stored value takes precedence; fall back to clinic name for legacy records missing the field.
    setClinicField(type === 'revolution_ehr' ? (config?.clinic ?? clinic?.name ?? '') : '')
    setShowPassword(false)
    setSaveError(null)
    setAttempted(false)
  }

  function closeModal() {
    setModalClinicId(null)
    setUsername('')
    setPassword('')
    setClinicField('')
    setSaveError(null)
    setAttempted(false)
  }

  async function handleSave() {
    setAttempted(true)
    setSaveError(null)
    if (!username.trim()) return
    if (modalType === 'revolution_ehr' && !clinicField.trim()) return
    if (!password || password === PASSWORD_PLACEHOLDER) {
      setSaveError('Please re-enter the password.')
      return
    }

    setSaving(true)
    try {
      const payload: SaveIntegrationCredentialsInput = {
        orgId,
        clinicId: modalClinicId,
        integrationType: modalType,
        username: username.trim(),
        password,
      }
      if (modalType === 'revolution_ehr') payload.clinic = clinicField.trim()
      await saveIntegrationCredentials(payload)
      closeModal()
      onIntegrationSaved()
      // A saved username may be another login, in another state; no save
      // opens the code setup — only the row's 2FA button does.
      if (modalType === 'eyemed') void loadEyemedStates()
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'message' in err
        ? String((err as { message: string }).message)
        : 'Failed to save credentials.'
      setSaveError(scrubVendorText(message))
    } finally {
      setSaving(false)
    }
  }

  function openDelete(clinicId: string, type: IntegrationType, title: string) {
    setDeleteTarget({ clinicId, type, title })
  }

  async function handleDelete() {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await deleteIntegrationCredentials({ orgId, clinicId: deleteTarget.clinicId, integrationType: deleteTarget.type })
      setDeleteTarget(null)
      onIntegrationSaved()
    } catch {
      // keep dialog open on error
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      <div className="integrations-view">
        <div className="integrations-view__header">
          <div>
            <div className="integrations-view__title-row">
              <svg className="integrations-view__icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
              <h2 className="integrations-view__title">Integrations</h2>
            </div>
            <p className="integrations-view__subtitle">Manage third-party integration credentials across all clinics</p>
          </div>
        </div>

        {clinics.length === 0 ? (
          <p className="integrations-view__empty">No clinics found. Create a clinic first.</p>
        ) : (
          <div className="integrations-clinic-list">
            {clinics.map(clinic => {
              const expanded = expandedClinicId === clinic.id
              const eyemed = eyemedRowStatus(eyemedRowInput(eyemedStates, clinic.id))
              const count = configuredCount(clinic, eyemed)
              const location = clinicLocation(clinic)
              return (
                <div key={clinic.id} className={`integrations-clinic ${expanded ? 'integrations-clinic--expanded' : ''}`}>
                  <button
                    className="integrations-clinic__row"
                    onClick={() => toggleClinic(clinic.id)}
                    aria-expanded={expanded}
                  >
                    <span className="integrations-clinic__chevron">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    </span>
                    <svg className="integrations-clinic__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 21h18M3 7l9-4 9 4M4 7v14M20 7v14M8 11h2m4 0h2M8 15h2m4 0h2" />
                    </svg>
                    <span className="integrations-clinic__name">{clinic.name}</span>
                    {location && <span className="integrations-clinic__location">{location}</span>}
                    <span className="integrations-clinic__dots">
                      {INTEGRATIONS.map(i => {
                        const modifier = i.type === 'eyemed' ? toneModifier(eyemed.tone) : 'active'
                        return (
                          <span
                            key={i.type}
                            className={`integrations-clinic__dot ${clinic.integrations?.[i.type] ? `integrations-clinic__dot--${modifier}` : ''}`}
                          />
                        )
                      })}
                      <span className="integrations-clinic__count">{count}/{INTEGRATIONS.length}</span>
                    </span>
                  </button>

                  {expanded && (
                    <div className="integrations-clinic__body">
                      {INTEGRATIONS.map(info => {
                        const config = clinic.integrations?.[info.type]
                        const configured = !!config
                        const isEyemed = info.type === 'eyemed'
                        // A saved EyeMed login shows where it stands instead of "Configured".
                        const statusLabel = !configured ? 'Not configured' : isEyemed ? eyemed.label : 'Configured'
                        const statusModifier = !configured ? '' : `integrations-row__status--${isEyemed ? toneModifier(eyemed.tone) : 'active'}`
                        return (
                          <div key={info.type} className="integrations-row">
                            <img className="integrations-row__logo" src={info.logo} alt={info.title} />
                            <div className="integrations-row__info">
                              <span className="integrations-row__title">{info.title}</span>
                              <span className="integrations-row__desc">{info.description}</span>
                            </div>
                            <span
                              className={`integrations-row__status ${statusModifier}`}
                              data-testid={isEyemed ? `eyemed-status-${clinic.id}` : undefined}
                            >
                              {statusLabel}
                            </span>
                            <div className="integrations-row__actions">
                              {configured ? (
                                <>
                                  {isEyemed && canWrite && (
                                    <button
                                      className="integrations-row__btn"
                                      onClick={() => setSetupFor({
                                        clinicId: clinic.id, clinicName: clinic.name, username: config?.username ?? '',
                                      })}
                                      aria-label="2FA setup"
                                      title="2FA setup"
                                    >
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                        <path d="m9 12 2 2 4-4" />
                                      </svg>
                                    </button>
                                  )}
                                  <button
                                    className="integrations-row__btn"
                                    onClick={() => openEdit(clinic.id, info.type)}
                                    aria-label={`Edit ${info.title}`}
                                    title="Edit credentials"
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                                    </svg>
                                  </button>
                                  {isAdmin && (
                                    <button
                                      className="integrations-row__btn integrations-row__btn--danger"
                                      onClick={() => openDelete(clinic.id, info.type, info.title)}
                                      aria-label={`Delete ${info.title}`}
                                      title="Delete credentials"
                                    >
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M3 6h18" />
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                                        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                      </svg>
                                    </button>
                                  )}
                                </>
                              ) : (
                                <button
                                  className="integrations-row__add"
                                  onClick={() => openAdd(clinic.id, info.type)}
                                >
                                  + Add
                                </button>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Credential modal */}
      {modalClinicId && modalInfo && (
        <div className="doctor-modal-overlay" onClick={closeModal}>
          <div className="doctor-modal" onClick={e => e.stopPropagation()}>
            <div className="doctor-modal__header">
              <div>
                <h3 className="doctor-modal__title">
                  {modalMode === 'add' ? 'Add' : 'Edit'} {modalInfo.title} Credentials
                </h3>
                {modalClinic && (
                  <p className="integrations-modal__clinic">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 21h18M3 7l9-4 9 4M4 7v14M20 7v14M8 11h2m4 0h2M8 15h2m4 0h2" />
                    </svg>
                    {modalClinic.name}
                  </p>
                )}
              </div>
              <button className="doctor-modal__close" onClick={closeModal} aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="add-doctor-form">
              <p className="integrations-modal__desc">Enter the credentials for {modalInfo.title} integration.</p>
              <div className="add-doctor-form__fields">
                <div className="form-field form-field--full">
                  <label className="form-field__label">Username *</label>
                  <input
                    className={`form-field__input${attempted && !username.trim() ? ' is-invalid' : ''}`}
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    placeholder="Enter username"
                    autoFocus
                    autoComplete="off"
                  />
                </div>
                {modalType === 'revolution_ehr' && (
                  <div className="form-field form-field--full">
                    <label className="form-field__label">Clinic *</label>
                    <input
                      className={`form-field__input${attempted && !clinicField.trim() ? ' is-invalid' : ''}`}
                      value={clinicField}
                      onChange={e => setClinicField(e.target.value)}
                      placeholder="Enter clinic"
                      autoComplete="off"
                    />
                  </div>
                )}
                <div className="form-field form-field--full">
                  <label className="form-field__label">Password *</label>
                  <div className="integrations-modal__password-wrap">
                    <input
                      className={`form-field__input${attempted && (!password || password === PASSWORD_PLACEHOLDER) ? ' is-invalid' : ''}`}
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      onFocus={() => { if (password === PASSWORD_PLACEHOLDER) setPassword('') }}
                      placeholder={modalMode === 'edit' ? 'Re-enter password' : 'Enter password'}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="integrations-modal__toggle-pw"
                      onClick={() => setShowPassword(v => !v)}
                      tabIndex={-1}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        {showPassword
                          ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" /><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" /><line x1="1" y1="1" x2="23" y2="23" /></>
                          : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>
                        }
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              {saveError && <p className="clinic-save-error">{saveError}</p>}
              <div className="add-doctor-form__actions">
                <span />
                <div className="add-doctor-form__actions-right">
                  <button className="add-doctor-form__cancel" onClick={closeModal} disabled={saving}>Cancel</button>
                  <button
                    className="check-btn add-doctor-form__submit"
                    disabled={saving}
                    onClick={handleSave}
                  >
                    {saving ? 'Saving…' : 'Save Credentials'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EyeMed code setup */}
      {setupFor && (
        <EyemedSetupModal
          orgId={orgId}
          clinicId={setupFor.clinicId}
          clinicName={setupFor.clinicName}
          username={setupFor.username}
          state={eyemedRowInput(eyemedStates, setupFor.clinicId)}
          onClose={() => setSetupFor(null)}
          onConfirmed={() => {
            setSetupFor(null)
            void loadEyemedStates()
          }}
          onSetUp={() => { void loadEyemedStates() }}
        />
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <div className="doctor-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="doctor-modal integrations-delete-modal" onClick={e => e.stopPropagation()}>
            <div className="doctor-modal__header">
              <h3 className="doctor-modal__title">Delete {deleteTarget.title} Credentials</h3>
              <button className="doctor-modal__close" onClick={() => setDeleteTarget(null)} aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="add-doctor-form">
              <p className="integrations-delete__text">
                This will remove the {deleteTarget.title} credentials for this clinic. The integration will stop working until new credentials are configured.
              </p>
              <div className="add-doctor-form__actions">
                <span />
                <div className="add-doctor-form__actions-right">
                  <button className="add-doctor-form__cancel" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</button>
                  <button
                    className="add-doctor-form__delete"
                    disabled={deleting}
                    onClick={handleDelete}
                  >
                    {deleting ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
